import {
  toastEnterDurationMs,
  toastExitDurationMs,
} from '@terminal/components/toasts-layout/toast-timings';
import { create } from '@terminal/utils/store/createStore';
import { ReactNode } from 'react';

export enum ToastPosition {
  Top = 'TOP',
  TopLeft = 'TOP_LEFT',
  TopRight = 'TOP_RIGHT',
  Bottom = 'BOTTOM',
  BottomLeft = 'BOTTOM_LEFT',
  BottomRight = 'BOTTOM_RIGHT',
}

export interface Toast {
  id: number | string;
  content: ReactNode;
  hideToast: boolean;
  timeout: number;
  position: ToastPosition;
  scope: string;
  exiting?: boolean;
}

const hideTimeouts = new Map<number | string, ReturnType<typeof setTimeout>>();
const exitTimeouts = new Map<number | string, ReturnType<typeof setTimeout>>();

interface ToastsStore {
  toasts: Toast[];
  maxToasts: number;
  showToast: (toast: Partial<Toast> & { content: ReactNode }) => void;
  removeToast: (toastId: number | string) => void;
  clearAllToasts: () => void;
  clearScopedToasts: (scope: string) => void;
  toastExists: (toastId: number | string) => boolean;
}

const defaultToastProps = {
  hideToast: true,
  timeout: 5000,
  position: ToastPosition.Top,
  scope: '',
};

export const useToasts = create<ToastsStore>((set, get) => ({
  toasts: [],
  maxToasts: 5,
  showToast: toast => {
    // setTimeout disables butch update (ReactDOM.flushSync won't work here)
    setTimeout(() => {
      if (get().toasts.some(t => t.id === toast.id)) {
        return;
      }

      const newToast = {
        id: new Date().getTime() + get().toasts.length,
        ...defaultToastProps,
        ...toast,
      };
      const newToasts = [...get().toasts, newToast];

      const presentToasts = get().toasts.filter(
        toast => toast.position === newToast.position
      );

      const toastsToSet =
        presentToasts.length === get().maxToasts
          ? newToasts.filter(t => t.id !== presentToasts[0].id)
          : newToasts;

      set({ toasts: toastsToSet });

      if (newToast.hideToast) {
        const hideAfterEnter = newToast.timeout + toastEnterDurationMs;
        const hideTimeout = setTimeout(() => {
          hideTimeouts.delete(newToast.id);
          get().removeToast(newToast.id);
        }, hideAfterEnter);
        hideTimeouts.set(newToast.id, hideTimeout);
      }
    }, 0);
  },

  removeToast: toastId => {
    const existing = hideTimeouts.get(toastId);
    if (existing) {
      clearTimeout(existing);
      hideTimeouts.delete(toastId);
    }
    const toasts = get().toasts;
    const toast = toasts.find(t => t.id === toastId);
    if (!toast || toast.exiting) return;
    set({
      toasts: toasts.map(t => (t.id === toastId ? { ...t, exiting: true } : t)),
    });
    const exitTimeout = setTimeout(() => {
      exitTimeouts.delete(toastId);
      set({ toasts: get().toasts.filter(t => t.id !== toastId) });
    }, toastExitDurationMs);
    exitTimeouts.set(toastId, exitTimeout);
  },
  clearAllToasts: () => {
    hideTimeouts.forEach(id => clearTimeout(id));
    hideTimeouts.clear();
    exitTimeouts.forEach(id => clearTimeout(id));
    exitTimeouts.clear();
    set({ toasts: [] });
  },
  toastExists: toastId => get().toasts.some(toast => toast.id === toastId),
  clearScopedToasts: (scope: string) => {
    const toasts = get().toasts;
    const toRemove = toasts.filter(t => t.scope === scope);
    toRemove.forEach(({ id }) => {
      const hideId = hideTimeouts.get(id);
      if (hideId) {
        clearTimeout(hideId);
        hideTimeouts.delete(id);
      }
      const exitId = exitTimeouts.get(id);
      if (exitId) {
        clearTimeout(exitId);
        exitTimeouts.delete(id);
      }
    });
    set({ toasts: get().toasts.filter(t => t.scope !== scope) });
  },
}));
